import React, { useState, useCallback } from 'react';
import Toast from './Toast';
import { generateResultImage } from '../utils/generateResultImage';

interface ShareButtonsProps {
  score: number;
  resultTitle: string;
  lifeExpectancy: number;
  resultCardRef: React.RefObject<HTMLDivElement>;
  quizTitle?: string;
  resultLabel?: string;
  resultUnit?: string;
}

const ShareButtons: React.FC<ShareButtonsProps> = ({
  score,
  resultTitle,
  lifeExpectancy,
  resultCardRef,
  quizTitle = '기대수명 테스트',
  resultLabel,
  resultUnit = '살',
}) => {
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  const siteUrl = window.location.origin;
  const displayLabel = resultLabel || `${lifeExpectancy}${resultUnit}`;
  const shareText = `나의 ${quizTitle} 결과: ${displayLabel}! "${resultTitle}" 등급이래요. 당신은?`;

  const showToastMessage = useCallback((msg: string) => {
    setToastMessage(msg);
    setShowToast(true);
  }, []);

  const handleNativeShare = async () => {
    if (!navigator.share) {
      showToastMessage('이 브라우저에서는 공유가 지원되지 않습니다.');
      return;
    }

    try {
      const shareData: ShareData = {
        title: `${quizTitle} 결과`,
        text: shareText,
        url: siteUrl,
      };

      if (resultCardRef.current) {
        const blob = await generateResultImage(resultCardRef.current);
        if (blob) {
          const file = new File([blob], `${quizTitle}-결과.png`, { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            shareData.files = [file];
          }
        }
      }

      await navigator.share(shareData);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        showToastMessage('공유에 실패했습니다.');
      }
    }
  };

  const handleKakaoShare = () => {
    if (!window.Kakao) {
      showToastMessage('카카오 SDK를 불러올 수 없습니다.');
      return;
    }

    if (!window.Kakao.isInitialized()) {
      window.Kakao.init('48aa905557b806530481b2adf205f8c5');
    }

    window.Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: `${quizTitle} 결과: ${displayLabel}`,
        description: `"${resultTitle}" 등급! 나도 테스트 해볼래?`,
        imageUrl: `${siteUrl}/opengragh2.png`,
        link: {
          mobileWebUrl: siteUrl,
          webUrl: siteUrl,
        },
      },
      buttons: [
        {
          title: '나도 테스트하기',
          link: {
            mobileWebUrl: siteUrl,
            webUrl: siteUrl,
          },
        },
      ],
    });
  };

  const handleTwitterShare = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(siteUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleFacebookShare = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${siteUrl}`);
      showToastMessage('링크가 복사되었습니다!');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = `${shareText}\n${siteUrl}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToastMessage('링크가 복사되었습니다!');
    }
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    maxWidth: '280px',
    padding: '14px 20px',
    margin: '6px 0',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontFamily: "'TTLaundryGothicB', sans-serif",
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    float: 'none',
    minWidth: 'unset',
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      margin: '20px 0',
    }}>
      <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', color: '#333' }}>
        결과 공유하기
      </p>

      {'share' in navigator && (
        <button onClick={handleNativeShare} style={{ ...buttonStyle, backgroundColor: '#6C63FF', color: '#fff' }}>
          📤 공유하기
        </button>
      )}

      <button onClick={handleKakaoShare} style={{ ...buttonStyle, backgroundColor: '#FEE500', color: '#3C1E1E' }}>
        💬 카카오톡 공유
      </button>

      <button onClick={handleTwitterShare} style={{ ...buttonStyle, backgroundColor: '#1DA1F2', color: '#fff' }}>
        🐦 트위터/X 공유
      </button>

      <button onClick={handleFacebookShare} style={{ ...buttonStyle, backgroundColor: '#4267B2', color: '#fff' }}>
        📘 페이스북 공유
      </button>

      <button onClick={handleCopyLink} style={{ ...buttonStyle, backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd' }}>
        🔗 링크 복사
      </button>

      <Toast message={toastMessage} show={showToast} onClose={() => setShowToast(false)} />
    </div>
  );
};

export default ShareButtons;
